import * as THREE from "three";
import React, { useMemo, useState } from "react";

import { params } from "../../../../../../redux/reducers/reducer";
import { extrudeSettings, leanRoofLoftTrimModel } from "../../../../../../utils/Functions";
import { MaterialTrim } from "../../../../CustomMaterial";

const TrimModel = () => {
    const [isEqualLeftHeight, setIsEqualLeftHeight] = useState(false);
    const [isEqualRightHeight, setIsEqualRightHeight] = useState(false);
    const [isEqualHeight, setIsEqualHeight] = useState(false);
    const [isEqualLeftLeanHeight, setIsEqualLeftLeanHeight] = useState(false);
    const [isEqualRightLeanHeight, setIsEqualRightLeanHeight] = useState(false);
    const [isAction, setIsAction] = useState(false);
    const [angle, setAngle] = useState(Math.atan(params.b_l_t_r_pB / 12));
    const [overHangLength, setOverHangLength] = useState(0);

    const trimThickness = (params.p_r_s === 1) ? 0.2 : 0.1;
    const cbFrontBackWallOffset = 0.16;

    // create state on is equal height
    const trimOffsetHeight = (params.p_r_p / 12) * 0.2;
    const leanRoofLeftHeight = (params.b_l_t_r_p / 12) * params.lean_p_w;
    const leanRoofRightHeight = (params.b_l_t_r_pR / 12) * params.leanR_p_w;
    const leanRoofHeight = (params.b_l_t_r_pB / 12) * params.leanB_p_w;

    const leftOffsetValue = useMemo(() => (
        (isEqualLeftLeanHeight && !isEqualRightLeanHeight && (params.p_w === params.leanB_p_d || Number(params.leantoAlignmentBack) === 3)) ||
        (isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w !== params.leanB_p_d && Number(params.leantoAlignmentBack) === 3)) ||
        (isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w === params.leanB_p_d))
      ) ? 0.53 : 0, [isEqualLeftLeanHeight, isEqualRightLeanHeight, isEqualHeight, params.p_w, params.leanB_p_d, params.leantoAlignmentBack]);
      
      const rightOffsetValue = useMemo(() => (
        (!isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w === params.leanB_p_d || Number(params.leantoAlignmentBack) === 2)) ||
        (isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w !== params.leanB_p_d && Number(params.leantoAlignmentBack) === 2)) ||
        (isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w === params.leanB_p_d))
      ) ? 0.53 : 0, [isEqualLeftLeanHeight, isEqualRightLeanHeight, isEqualHeight, params.p_w, params.leanB_p_d, params.leantoAlignmentBack]);

    const roofSideTrimModel = useMemo(() => {
        const model = new THREE.Shape();
        model.moveTo(-0.07, (-0.07 * params.b_l_t_r_pB) / 12);
        model.lineTo(params.leanB_p_w - (isAction && trimThickness), (params.b_l_t_r_pB / 12) * params.leanB_p_w - (isAction && trimOffsetHeight));
        model.lineTo(
            params.leanB_p_w - (isAction && trimThickness),
            (params.b_l_t_r_pB / 12) * params.leanB_p_w - (isAction && trimOffsetHeight) - cbFrontBackWallOffset - 0.04
        );
        model.lineTo(-0.07, (-0.07 * params.b_l_t_r_pB) / 12 - cbFrontBackWallOffset - 0.04);
        model.closePath();

        return model;
    }, [params.b_l_t_r_pB, params.leanB_p_w, isAction]);

    const roofFrontTrimModel = useMemo(() => {
        const model = new THREE.Shape();

        model.moveTo(-0.02, (-params.b_l_t_r_pB / 12) * 0.02);
        model.lineTo(0.05, (params.b_l_t_r_pB / 12) * 0.1);
        model.lineTo(0.05, (params.b_l_t_r_pB / 12) * 0.1 - 0.2);
        model.lineTo(-0.02, (-params.b_l_t_r_pB / 12) * 0.02 - 0.2);
        model.closePath();

        return model;
    }, [params.b_l_t_r_pB]);

    const roofFrontTrimLength = useMemo(() => {
        let length;

        if (params.add_left_back_lean_porch !== true && params.add_right_back_lean_porch !== true) {
            length = params.leanB_p_d + 0.04;
        } else if (
            (params.add_left_back_lean_porch !== true && params.add_right_back_lean_porch === true) ||
            (params.add_left_back_lean_porch === true && params.add_right_back_lean_porch !== true)
        ) {
            length = params.leanB_p_d + params.leanB_p_w + 0.04;
        } else {
            length = params.leanB_p_d + 2 * params.leanB_p_w + 0.04;
        }
        return length;
    }, [params.add_left_back_lean_porch, params.add_right_back_lean_porch, params.leanB_p_d, params.leanB_p_w]);

    const roofFrontTrimPosX = useMemo(() => {
        if (params.add_left_back_lean_porch === true && params.add_right_back_lean_porch === true) {
            return roofFrontTrimLength / 2;
        } else if (params.add_left_back_lean_porch === true && params.add_right_back_lean_porch !== true) {
            return roofFrontTrimLength / 2 - params.leanB_p_w / 2;
        } else if (params.add_left_back_lean_porch !== true && params.add_right_back_lean_porch === true) {
            return roofFrontTrimLength / 2 + params.leanB_p_w / 2;
        } else {
            return roofFrontTrimLength / 2;
        }
    }, [params.add_left_back_lean_porch, params.add_right_back_lean_porch, params.leanB_p_w, roofFrontTrimLength]);

    useMemo(() => {
        if (params.p_h === leanRoofLeftHeight + params.lean_p_h) {
            setIsEqualLeftHeight(true);
        } else {
            setIsEqualLeftHeight(false);
        }
        if (params.p_h === leanRoofRightHeight + params.leanR_p_h) {
            setIsEqualRightHeight(true);
        } else {
            setIsEqualRightHeight(false);
        }
        if(params.p_h === leanRoofHeight + params.leanB_p_h) setIsEqualHeight(true);   
        else setIsEqualHeight(false)
        if(params.add_left_lean && (leanRoofHeight + params.leanB_p_h === leanRoofLeftHeight + params.lean_p_h) && ((params.p_d === params.lean_p_d) || Number(params.leantoAlignmentLeft) === 3))
         setIsEqualLeftLeanHeight(true);
        else setIsEqualLeftLeanHeight(false)
        if(params.add_right_lean && (leanRoofHeight + params.leanB_p_h === leanRoofRightHeight + params.leanR_p_h) && ((params.p_d === params.leanR_p_d) || Number(params.leantoAlignmentRight) === 3))
         setIsEqualRightLeanHeight(true);
        else setIsEqualRightLeanHeight(false)
    }, [params.p_h, params.lean_p_h, leanRoofLeftHeight, leanRoofRightHeight, leanRoofHeight, params.leanR_p_h, params.b_l_t_r_p, params.b_l_t_r_pB, params.leantoAlignmentLeft, params.leantoAlignmentRight, params.b_l_t_r_pR, params.leanB_p_h, params.add_left_lean, params.add_right_lean, params.p_d, params.lean_p_d, params.leanR_p_d ])

    useMemo(() => {
        if ((isEqualLeftHeight && params.add_right_back_lean_porch) || (isEqualRightHeight && params.add_left_back_lean_porch)) {
            setAngle(Math.atan((leanRoofLeftHeight - trimOffsetHeight) / (params.leanB_p_w - trimThickness)));
            setIsAction(true);
        } else {
            setAngle(Math.atan(params.b_l_t_r_pB / 12));
            setIsAction(false);
        }
    }, [isEqualLeftHeight, isEqualRightHeight, params.add_right_back_lean_porch, params.add_left_back_lean_porch, leanRoofLeftHeight, params.b_l_t_r_pB, params.leanB_p_w, trimOffsetHeight])
    
    useMemo(() => {

        if (params.p_r_s === 2 || params.p_r_s === 1) {
            if(params.p_r_s === 2) setOverHangLength(0.75); else setOverHangLength(0.75);

        } else if (params.p_r_s === 3) {

            if (params.o_v_h_n_g_e === true) {
                setOverHangLength(0.9)
            } else {
                setOverHangLength(0.75)
            }
        }
    }, [params.p_r_s, params.o_v_h_n_g_e])

    return (
        <group>
            { !params.add_left_back_lean_porch && 
                ( params.p_r_s === 1 ?
                    <mesh name='loft-roof-trim-left' position={[-(params.leanB_p_d / 2 - trimThickness + overHangLength + 0.01) + leftOffsetValue + 0.15, params.leanB_p_h - 0.05, -(params.p_d / 2 + params.leanB_p_w + cbFrontBackWallOffset + 0.07)]} rotation={[0, -Math.PI / 2, 0]}>
                        <extrudeGeometry args={[leanRoofLoftTrimModel(params.b_l_t_r_pB, params.leanB_p_w + 0.1), extrudeSettings(trimThickness)]} />
                        <MaterialTrim visibleSection={"backLean"} />
                    </mesh>
                    :
                    <mesh name='non-loft-roof-trim-left' position={[-(params.leanB_p_d / 2 - trimThickness/6), params.leanB_p_h, -(params.p_d / 2 + params.leanB_p_w + cbFrontBackWallOffset)]} rotation={[0, -Math.PI / 2, 0]}>
                        <extrudeGeometry args={[roofSideTrimModel, extrudeSettings(trimThickness)]} />
                        <MaterialTrim visibleSection={"backLean"} />
                    </mesh>
                )
            }
            { !params.add_right_back_lean_porch &&
                ( params.p_r_s === 1 ? 
                    <mesh name='loft-roof-trim-right' position={[(params.leanB_p_d / 2 + overHangLength + 0.01) - rightOffsetValue - 0.15, params.leanB_p_h - 0.05, -(params.p_d / 2 + params.leanB_p_w + cbFrontBackWallOffset + 0.07)]} rotation={[0, -Math.PI / 2, 0]}>
                        <extrudeGeometry args={[leanRoofLoftTrimModel(params.b_l_t_r_pB, params.leanB_p_w + 0.1), extrudeSettings(trimThickness)]} />
                        <MaterialTrim visibleSection={"backLean"} />
                    </mesh>
                    :
                    <mesh name='non-loft-roof-trim-right' position={[(params.leanB_p_d / 2  + trimThickness/1.2), params.leanB_p_h, -(params.p_d / 2 + params.leanB_p_w + cbFrontBackWallOffset)]} rotation={[0, -Math.PI / 2, 0]}>
                        <extrudeGeometry args={[roofSideTrimModel, extrudeSettings(trimThickness)]} />
                        <MaterialTrim visibleSection={"backLean"} />
                    </mesh>
                )
            }
            { params.p_r_s !== 1 && (
                <mesh name='non-loft-roof-trim-front' position={[roofFrontTrimPosX, params.leanB_p_h - 0.05 * (params.b_l_t_r_pB / 12), -(params.p_d / 2 + params.leanB_p_w + cbFrontBackWallOffset + 0.05)]} rotation={[0, -Math.PI / 2, 0]}>
                    <extrudeGeometry args={[roofFrontTrimModel, extrudeSettings(roofFrontTrimLength)]} />
                    <MaterialTrim visibleSection={"backLean"} />
                </mesh>
            )}
        </group>
    );
};

export default TrimModel;
